import { ClientFlowType } from '../providers/GatekeeperNetworkService/GatekeeperNetworkServiceData';
import { Routes } from './routes';

/** The supported actions */
export enum Action {
  ISSUANCE = 'issuance',
  REFRESH = 'refresh',
  STATUS = 'status',
  REVOKE = 'revoke',
  TOKEN_FROZEN = 'tokenFrozen',
  TOKEN_ACTIVE = 'tokenActive',
  TOKEN_REVOKED = 'tokenRevoked',
  TOKEN_REJECTED = 'tokenRejected',
  TOKEN_IN_REVIEW = 'tokenInReview',
  // eslint-disable-next-line @typescript-eslint/no-duplicate-enum-values
  TOKEN_REFRESH_IN_REVIEW = 'tokenRefreshInReview',
  FAILED_IP_CHECK = 'failedIpCheck',
  PROOF_OF_WALLET_OWNERSHIP = 'proofOfWalletOwnership',
  THEME_DEMO = 'themeDemo',
  CHAIN_ERROR = 'chainError',
  LINK = 'link',
  CREDENTIAL_EXPIRED = 'credentialExpired',
  SIGN_TRANSACTION = 'signTransaction',
  RESUME_TRANSACTION_SIGNING = 'startPreApprovedTransaction',
}

/** Action providers */
export type EventProvider = Provider | CaptchaProvider;
export enum Provider {
  SIP = 'sip',
  FACE_IDENTITY = 'faceIdentity',
  WEB = 'web',
  CAPTCHA = 'captcha',
  FACE_UNIQUENESS = 'faceUniqueness',
  WALLET = 'wallet',
  MARKETING = 'marketing',
}

export enum CaptchaProvider {
  RECAPTCHA = 'recaptcha',
  HCAPTCHA = 'hcaptcha',
}

// These are keys used for flow-specific translations, e.g. title_livenessPass
export enum UserFlow {
  GENESIS = 'genesisPass',
  BLUE_GENESIS = 'genesisNoPiiPass',
  CAPTCHA = 'captchaPass',
  DOCUMENT = 'documentPass', // Old Nile
  BLUE_NILE = 'documentNoPiiPass',
  YUKON = 'yukonPass', // i.e. Genesis with more country restrictions incl. USA
  UNIQUENESS = 'uniquenessPass',
  LIVENESS = 'livenessPass', // Old Vaal
  AGE_CHECK_13 = 'ageCheck13Pass',
  FACE_AGE_CHECK_13 = 'faceAgeCheck13Pass',
  CIVIC_DEMO = 'testPass',
}

// Map the GKN flow to the user flow
export const networkMapping: Record<ClientFlowType, UserFlow> = {
  age13: UserFlow.AGE_CHECK_13,
  faceAge13: UserFlow.FACE_AGE_CHECK_13,
  liveness: UserFlow.LIVENESS,
  demo: UserFlow.CIVIC_DEMO,
  uniqueness: UserFlow.UNIQUENESS,
  captcha: UserFlow.CAPTCHA,
  document: UserFlow.DOCUMENT,
  genesis: UserFlow.GENESIS,
  yukon: UserFlow.YUKON,
};

/** Custom provider configuration */
export interface ProviderConfig {
  authenticationDisabled?: boolean;
  providerExplainerRoute?: Routes;
  authenticationRequired?: boolean;
  showSelfDeclaration: boolean;
  route: Routes;
}

/** SIP provider configuration */
export type SipConfig = ProviderConfig & {
  appId?: string;
  // For the web provider, the template ref is fetched from the Gatekeeper Network Service at runtime.
  // The field is kept here for backwards-compatibility with the SIP flow (QR code) which uses v1 credentials.
  scopeRequestTemplate?: string;
  credentialsIssuerDid?: string;
};

export interface CaptchaConfig extends ProviderConfig {
  captchaProvider: CaptchaProvider;
}

export type ProviderConfigs = Partial<Record<Provider, ProviderConfig>>;

export type FlowConfig = {
  legacyPOWOEnabled?: boolean;
};

export type ActionConfig = {
  route: Routes;
  config?: FlowConfig;
  providers?: ProviderConfigs;
};

export type ClientFlowConfig = Partial<Record<Action, ActionConfig>>;

export type ClientFlowConfigs = Record<string, ClientFlowConfig>;
